import React from "react";
import { Oval } from "react-loader-spinner";
import toast from "react-hot-toast";

import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import {
  DumpsterSize,
  DumpWeightByMaterial,
  DUMP_WEIGHT_MATERIALS_BY_SIZE,
} from "../../../../../models";
import { getDumpWeight, updateDumpWeight } from "../../../settingsSlice";

import "./DumpWeightSection.scss";

// Dumpster sizes in display order, with short labels (matches PricingTab).
const SIZES: Array<{ size: DumpsterSize; label: string }> = [
  { size: DumpsterSize.SIZE_10, label: "10 yd" },
  { size: DumpsterSize.SIZE_15, label: "15 yd" },
  { size: DumpsterSize.SIZE_CLEAN_FILL, label: "Clean Fill" },
];

// form state: size -> material key -> raw string.
type FormState = Record<string, Record<string, string>>;

function toForm(w: DumpWeightByMaterial): FormState {
  const form: FormState = {};
  for (const { size } of SIZES) {
    form[size] = {};
    for (const { key } of DUMP_WEIGHT_MATERIALS_BY_SIZE[size]) {
      const v = w[size]?.[key];
      form[size][key] = v == null ? "" : String(v);
    }
  }
  return form;
}

// Valid = a number > 0 (weights are tons and may be fractional; mirrors backend).
const isValidWeight = (raw: string): boolean => {
  const n = Number(raw.trim());
  return raw.trim() !== "" && !Number.isNaN(n) && n > 0;
};

const eachField = (fn: (size: DumpsterSize, key: string) => boolean): boolean =>
  SIZES.every(({ size }) =>
    DUMP_WEIGHT_MATERIALS_BY_SIZE[size].every(({ key }) => fn(size, key))
  );

const anyField = (fn: (size: DumpsterSize, key: string) => boolean): boolean =>
  SIZES.some(({ size }) =>
    DUMP_WEIGHT_MATERIALS_BY_SIZE[size].some(({ key }) => fn(size, key))
  );

export function DumpWeightSection() {
  const dispatch = useAppDispatch();
  const dumpWeight = useAppSelector((s) => s.settings.dumpWeight);
  const loadAPI = useAppSelector((s) => s.settings.getDumpWeight);
  const saveAPI = useAppSelector((s) => s.settings.updateDumpWeight);

  const [form, setForm] = React.useState<FormState | undefined>(undefined);

  React.useEffect(() => {
    dispatch(getDumpWeight());
  }, [dispatch]);

  // Seed / reset the form on initial load and after the post-save refetch.
  React.useEffect(() => {
    if (dumpWeight) setForm(toForm(dumpWeight));
  }, [dumpWeight]);

  React.useEffect(() => {
    if (!loadAPI.loading && loadAPI.error) {
      toast.error("Failed to load dump weights. Please try again.");
    }
  }, [loadAPI.loading, loadAPI.error]);

  const setField = (size: DumpsterSize, key: string, value: string) =>
    setForm((prev) =>
      prev ? { ...prev, [size]: { ...prev[size], [key]: value } } : prev
    );

  const allValid = form
    ? eachField((size, key) => isValidWeight(form[size][key]))
    : false;
  const isDirty =
    !!form &&
    !!dumpWeight &&
    anyField(
      (size, key) => form[size][key].trim() !== String(dumpWeight[size]?.[key])
    );
  const canSave = allValid && isDirty && !saveAPI.loading;

  const onSave = async () => {
    if (!form || !canSave) return;
    const value = {} as DumpWeightByMaterial;
    for (const { size } of SIZES) {
      value[size] = {};
      for (const { key } of DUMP_WEIGHT_MATERIALS_BY_SIZE[size]) {
        value[size][key] = Number(form[size][key]);
      }
    }
    const result = await dispatch(updateDumpWeight(value));
    if (updateDumpWeight.fulfilled.match(result)) {
      toast.success("Dump weights saved.");
    } else {
      toast.error("Failed to save dump weights. Please try again.");
    }
  };

  const heading = (
    <div className="section-heading">
      <h3>Dump Weights</h3>
      <span className="section-hint">
        Default billed weight (tons) of a full container, by dumpster size and
        material. The optimizer uses these to estimate dump fees.
      </span>
    </div>
  );

  if (loadAPI.loading && !form) {
    return (
      <section className="ro-section dump-weight-section">
        {heading}
        <div className="section-loading">
          <Oval color="#0A60BB" height={28} width={28} secondaryColor="#DAE0E6" />
        </div>
      </section>
    );
  }

  return (
    <section className="ro-section dump-weight-section">
      {heading}

      {SIZES.map(({ size, label }) => (
        <div className="size-group" key={size}>
          <h4 className="size-label">{label}</h4>
          <div className="material-grid">
            {DUMP_WEIGHT_MATERIALS_BY_SIZE[size].map(
              ({ key, label: materialLabel }) => {
                const raw = form ? form[size][key] : "";
                const invalid = !isValidWeight(raw);
                const inputId = `dump-weight-${size}-${key}`;
                const ariaLabel = `${label} ${materialLabel}`;
                return (
                  <div
                    key={key}
                    className={`field-container${invalid ? " has-error" : ""}`}
                  >
                    <label className="label" htmlFor={inputId}>
                      {materialLabel}
                    </label>
                    <div className="weight-input">
                      <input
                        id={inputId}
                        type="number"
                        min={0}
                        step="0.1"
                        value={raw}
                        aria-label={ariaLabel}
                        aria-invalid={invalid}
                        onChange={(e) => setField(size, key, e.target.value)}
                      />
                      <span className="suffix">tons</span>
                    </div>
                    {invalid && (
                      <span className="form-error">
                        Enter a weight greater than 0.
                      </span>
                    )}
                  </div>
                );
              }
            )}
          </div>
        </div>
      ))}

      <div className="ro-footer">
        <button
          type="button"
          className="submit-button"
          onClick={onSave}
          disabled={!canSave}
        >
          {saveAPI.loading ? "Saving…" : "Save changes"}
        </button>
      </div>
    </section>
  );
}
